/**
 * Form fields — the one recipe for every form on the web-people screens
 * (2026-09 redesign, Stripe / Attio):
 *
 *   Label · optional              13/500 ink-2, "optional" in ink-3
 *   ┌──────────────────────────┐  36px control, 8px radius, slate-300 hairline;
 *   │                          │  brand border + a soft 3px halo on focus;
 *   └──────────────────────────┘  a red border + halo when invalid
 *   Help text / ⓘ error           12px — the error REPLACES the hint so the
 *                                 form never jumps by a line on submit
 *
 * Required fields are the norm, so it is the OPTIONAL ones that get marked,
 * with a translated word rather than an asterisk. Every control is wired to
 * its label, hint and error (`htmlFor`, `aria-describedby`, `aria-invalid`)
 * through `useId`, so callers never hand-roll ids.
 *
 * The primitives invent no strings: the caller passes the translated label,
 * hint, error and "optional" marker from its own namespace.
 */
import {
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type Ref,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";
import { IconChevronDown } from "@/components/common/icons";
import { IconAlertCircle } from "./icons";

const CONTROL =
  "block w-full rounded-lg border bg-white text-sm text-slate-900 placeholder:text-slate-400 " +
  "transition-[border-color,box-shadow] duration-150 focus:outline-none " +
  "disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500";
const CONTROL_OK =
  "border-slate-300 hover:border-slate-400 focus:border-brand-500 focus:ring-[3px] focus:ring-brand-500/15";
const CONTROL_BAD = "border-tag-crit-fg focus:ring-[3px] focus:ring-tag-crit-fg/15";

/** Classes for a text-like control. `size="sm"` (32px) is for inline table
 *  editors; the default (36px) is for forms. */
export function controlClass(invalid = false, extra = "", size: "sm" | "md" = "md"): string {
  const box = size === "sm" ? "h-8 px-2.5 text-[13px]" : "h-9 px-3";
  return `${CONTROL} ${box} ${invalid ? CONTROL_BAD : CONTROL_OK} ${extra}`;
}

export interface FieldA11y {
  id: string;
  "aria-describedby"?: string;
  "aria-invalid"?: true;
}

interface FieldProps {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  /** The translated "optional" marker, shown after the label. */
  optional?: ReactNode;
  /** Keep the label for screen readers only (a table cell, a search box). */
  hideLabel?: boolean;
  className?: string;
  children: (a11y: FieldA11y) => ReactNode;
}

export function Field({ label, hint, error, optional, hideLabel = false, className = "", children }: FieldProps) {
  const id = useId();
  const messageId = `${id}-msg`;
  const hasMessage = Boolean(error) || Boolean(hint);
  return (
    <div className={className}>
      <label
        htmlFor={id}
        className={
          hideLabel ? "sr-only" : "flex items-baseline gap-1.5 mb-1.5 text-[13px] font-medium text-slate-700"
        }
      >
        {label}
        {optional && !hideLabel && <span className="text-xs font-normal text-slate-500">{optional}</span>}
      </label>
      {children({
        id,
        "aria-describedby": hasMessage ? messageId : undefined,
        "aria-invalid": error ? true : undefined,
      })}
      <FieldMessage id={messageId} hint={hint} error={error} />
    </div>
  );
}

/** The line under a control: the error when there is one, else the hint. */
export function FieldMessage({ id, hint, error }: { id?: string; hint?: ReactNode; error?: ReactNode }) {
  if (error) {
    return (
      <p id={id} className="mt-1.5 flex items-start gap-1 text-xs leading-4 text-tag-crit-fg">
        <IconAlertCircle className="w-3.5 h-3.5 shrink-0" />
        <span>{error}</span>
      </p>
    );
  }
  if (hint) {
    return (
      <p id={id} className="mt-1.5 text-xs leading-4 text-slate-500">
        {hint}
      </p>
    );
  }
  return null;
}

/**
 * A group of controls that share one label (working days, a time range).
 * A fieldset + legend, so a screen reader announces the group's name.
 */
export function FieldGroup({
  legend,
  hint,
  error,
  optional,
  className = "",
  children,
}: {
  legend: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  optional?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  const id = useId();
  return (
    <fieldset className={`min-w-0 ${className}`} aria-describedby={error || hint ? `${id}-msg` : undefined}>
      <legend className="flex items-baseline gap-1.5 mb-1.5 text-[13px] font-medium text-slate-700">
        {legend}
        {optional && <span className="text-xs font-normal text-slate-500">{optional}</span>}
      </legend>
      {children}
      <FieldMessage id={`${id}-msg`} hint={hint} error={error} />
    </fieldset>
  );
}

type OwnFieldProps = {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  optional?: ReactNode;
  hideLabel?: boolean;
  className?: string;
};

type TextFieldProps = OwnFieldProps &
  Omit<InputHTMLAttributes<HTMLInputElement>, "id" | "className" | "size"> & {
    inputClassName?: string;
    inputRef?: Ref<HTMLInputElement>;
    /** A control inside the field's end edge (show-password, clear). */
    endAdornment?: ReactNode;
  };

export function TextField({
  label,
  hint,
  error,
  optional,
  hideLabel,
  className,
  inputClassName = "",
  inputRef,
  endAdornment,
  ...input
}: TextFieldProps) {
  return (
    <Field label={label} hint={hint} error={error} optional={optional} hideLabel={hideLabel} className={className}>
      {(a11y) =>
        endAdornment ? (
          // The wrapper takes the input's own direction, so the adornment and
          // the padding reserved for it land on the same edge — an LTR field
          // (a password, an email) on a Hebrew page keeps its eye on the right.
          <div className="relative" dir={input.dir}>
            <input
              ref={inputRef}
              {...a11y}
              {...input}
              className={controlClass(Boolean(error), `pe-10 ${inputClassName}`)}
            />
            <div className="absolute inset-y-0 end-0 flex items-center pe-1">{endAdornment}</div>
          </div>
        ) : (
          <input ref={inputRef} {...a11y} {...input} className={controlClass(Boolean(error), inputClassName)} />
        )
      }
    </Field>
  );
}

type TextAreaFieldProps = OwnFieldProps &
  Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "id" | "className"> & { inputClassName?: string };

export function TextAreaField({
  label,
  hint,
  error,
  optional,
  hideLabel,
  className,
  inputClassName = "",
  ...input
}: TextAreaFieldProps) {
  return (
    <Field label={label} hint={hint} error={error} optional={optional} hideLabel={hideLabel} className={className}>
      {(a11y) => (
        <textarea
          {...a11y}
          {...input}
          className={`${CONTROL} ${error ? CONTROL_BAD : CONTROL_OK} px-3 py-2 leading-5 resize-y min-h-[72px] ${inputClassName}`}
        />
      )}
    </Field>
  );
}

type SelectInputProps = Omit<SelectHTMLAttributes<HTMLSelectElement>, "size"> & {
  invalid?: boolean;
  size?: "sm" | "md";
  /** Classes for the wrapper (width), not the select. */
  wrapperClassName?: string;
};

/** A native select with our chrome: no OS arrow, a chevron at the end edge. */
export function SelectInput({
  invalid = false,
  size = "md",
  className = "",
  wrapperClassName = "",
  children,
  ...select
}: SelectInputProps) {
  return (
    <div className={`relative ${wrapperClassName}`}>
      <select
        {...select}
        className={`${controlClass(invalid, `appearance-none cursor-pointer pe-9 ${className}`, size)}`}
      >
        {children}
      </select>
      <IconChevronDown
        className={`pointer-events-none absolute top-1/2 -translate-y-1/2 end-2.5 text-slate-500 ${
          size === "sm" ? "w-3.5 h-3.5" : "w-4 h-4"
        }`}
      />
    </div>
  );
}

type SelectFieldProps = OwnFieldProps &
  Omit<SelectHTMLAttributes<HTMLSelectElement>, "id" | "className" | "size"> & { selectClassName?: string };

export function SelectField({
  label,
  hint,
  error,
  optional,
  hideLabel,
  className,
  selectClassName = "",
  children,
  ...select
}: SelectFieldProps) {
  return (
    <Field label={label} hint={hint} error={error} optional={optional} hideLabel={hideLabel} className={className}>
      {(a11y) => (
        <SelectInput {...a11y} {...select} invalid={Boolean(error)} className={selectClassName}>
          {children}
        </SelectInput>
      )}
    </Field>
  );
}

/** A read-only fact in a detail panel: small label over the value. */
export function Fact({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium text-slate-500">{label}</dt>
      <dd className="mt-0.5 text-sm text-slate-900 truncate">{children}</dd>
    </div>
  );
}
